import LegalLayout, { Section, Card, Grid, InfoBox, ContactCTA } from "@/components/chalky/LegalLayout"

interface Props {
  last_updated: string
}

export default function CGV({ last_updated }: Props) {
  return (
    <LegalLayout
      title="Conditions Générales"
      titleHighlight="de Vente"
      subtitle="Conditions générales de vente applicables à tous les services proposés par CHALKY EURL"
      lastUpdated={last_updated}
    >
      <Section number="1" title="Présentation de l'entreprise">
        <Grid>
          <Card>
            <div className="space-y-3">
              <div>
                <p className="text-xs uppercase text-slate-500 font-medium mb-1">Raison sociale</p>
                <p className="text-slate-200">CHALKY EURL</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500 font-medium mb-1">Gérant</p>
                <p className="text-slate-200">Florent Guilbaud</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500 font-medium mb-1">SIRET</p>
                <p className="text-slate-200">93435243600011</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="space-y-3">
              <div>
                <p className="text-xs uppercase text-slate-500 font-medium mb-1">Siège social</p>
                <p className="text-slate-200">26 B rue Aristide Briand<br />44230 Saint-Sébastien-sur-Loire, France</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500 font-medium mb-1">Contact</p>
                <p className="text-slate-200">hello@chalky.fr</p>
                <p className="text-slate-200">+33 6 74 23 60 80</p>
              </div>
            </div>
          </Card>
        </Grid>
      </Section>

      <Section number="2" title="Objet et champ d'application">
        <p className="text-slate-400 leading-relaxed mb-6">
          Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre CHALKY EURL et ses clients pour la fourniture de services de développement web, d'applications mobiles et de conseil technologique.
        </p>
        <Card>
          <p className="font-medium text-white mb-3">Services proposés</p>
          <ul className="list-disc pl-5 space-y-2 text-sm text-slate-400">
            <li>Développement d'applications web sur mesure</li>
            <li>Création d'applications mobiles</li>
            <li>Consulting et conseil technologique</li>
            <li>Maintenance et support technique</li>
            <li>Formation et accompagnement</li>
          </ul>
        </Card>
      </Section>

      <Section number="3" title="Devis et commandes">
        <Grid>
          <Card className="text-center">
            <i className="fa-solid fa-check text-slate-500 mb-3 block" />
            <p className="font-medium text-white mb-2">Devis gratuits</p>
            <p className="text-sm text-slate-400">Tous nos devis sont établis gratuitement et sans engagement</p>
          </Card>
          <Card className="text-center">
            <i className="fa-solid fa-clock text-slate-500 mb-3 block" />
            <p className="font-medium text-white mb-2">Validité des devis</p>
            <p className="text-sm text-slate-400">Valables 30 jours à compter de leur émission</p>
          </Card>
        </Grid>
        <p className="text-slate-400 mt-6 leading-relaxed">
          Les devis détaillent précisément les prestations à réaliser, les délais d'exécution et les conditions tarifaires. L'acceptation d'un devis vaut commande ferme et définitive.
        </p>
      </Section>

      <Section number="4" title="Prix et modalités de paiement">
        <Grid>
          <Card>
            <p className="font-medium text-white mb-3">
              <i className="fa-solid fa-building-columns text-slate-500 mr-2" />
              Modalités de paiement
            </p>
            <p className="text-sm text-slate-300 mb-2 font-medium">Virement bancaire uniquement</p>
            <p className="text-sm text-slate-400">
              Tous les paiements s'effectuent exclusivement par virement bancaire. Les coordonnées bancaires sont communiquées sur chaque facture.
            </p>
          </Card>
          <Card className="text-center">
            <p className="font-medium text-white mb-3">Délais de paiement</p>
            <p className="text-3xl font-bold text-white mb-2">30 jours</p>
            <p className="text-sm text-slate-400 mb-4">À compter de la date d'émission de la facture</p>
            <div className="bg-white/[0.03] border border-white/[0.07] rounded-lg p-3">
              <p className="text-slate-300 font-medium text-sm">Pénalités de retard : 20% du montant dû</p>
            </div>
          </Card>
        </Grid>
        <p className="text-slate-400 mt-6 leading-relaxed text-sm">
          Les prix sont exprimés en euros hors taxes. La TVA sera ajoutée le cas échéant selon la réglementation en vigueur.
        </p>
      </Section>

      <Section number="5" title="Exécution des prestations">
        <p className="text-slate-400 leading-relaxed mb-6">
          Les prestations sont exécutées conformément aux spécifications définies dans le devis accepté. Les délais d'exécution sont donnés à titre indicatif et dépendent notamment de la collaboration du client.
        </p>
        <Grid>
          <Card>
            <p className="font-medium text-white mb-3">Obligations de CHALKY</p>
            <ul className="list-disc pl-4 text-sm text-slate-400 space-y-1">
              <li>Respecter les spécifications techniques convenues</li>
              <li>Mettre en œuvre les meilleures pratiques du secteur</li>
              <li>Assurer un suivi régulier du projet</li>
              <li>Livrer les prestations dans les délais convenus</li>
            </ul>
          </Card>
          <Card>
            <p className="font-medium text-white mb-3">Obligations du client</p>
            <ul className="list-disc pl-4 text-sm text-slate-400 space-y-1">
              <li>Fournir les éléments nécessaires à la réalisation</li>
              <li>Valider les étapes intermédiaires dans les délais</li>
              <li>Effectuer les paiements aux échéances convenues</li>
              <li>Collaborer activement au projet</li>
            </ul>
          </Card>
        </Grid>
      </Section>

      <Section number="6" title="Propriété intellectuelle">
        <Card className="text-center mb-6">
          <i className="fa-solid fa-gavel text-slate-500 mb-3 text-lg block" />
          <p className="text-lg font-semibold text-white mb-2">CHALKY conserve tous les droits</p>
          <p className="text-slate-400 text-sm">
            CHALKY EURL reste propriétaire de tous les droits de propriété intellectuelle sur les créations réalisées.
          </p>
        </Card>
        <Grid>
          <Card>
            <p className="font-medium text-white mb-3">
              <i className="fa-solid fa-check text-slate-500 mr-2" />
              Droits du client
            </p>
            <ul className="list-disc pl-4 text-sm text-slate-400 space-y-2">
              <li>Licence d'utilisation exclusive pour l'usage convenu</li>
              <li>Droit de modification pour les besoins de l'exploitation</li>
              <li>Droit d'adaptation technique nécessaire</li>
              <li>Usage commercial selon les termes du contrat</li>
            </ul>
          </Card>
          <Card>
            <p className="font-medium text-white mb-3">
              <i className="fa-solid fa-xmark text-slate-500 mr-2" />
              Restrictions
            </p>
            <ul className="list-disc pl-4 text-sm text-slate-400 space-y-2">
              <li>Aucune cession de droits de propriété</li>
              <li>Interdiction de revendre le code source</li>
              <li>Pas de licence pour créer des œuvres dérivées</li>
              <li>Obligation de mentionner CHALKY comme créateur</li>
            </ul>
          </Card>
        </Grid>
        <InfoBox icon="fa-solid fa-scale-balanced" title="Clause importante">
          Le client obtient une licence d'utilisation du produit livré pour ses besoins propres, mais CHALKY conserve l'entière propriété intellectuelle.
        </InfoBox>
      </Section>

      <Section number="7" title="Garanties et responsabilité">
        <p className="text-slate-400 leading-relaxed mb-6">
          CHALKY s'engage à mettre en œuvre tous les moyens nécessaires pour assurer la bonne exécution des prestations conformément aux règles de l'art.
        </p>
        <Grid cols={3}>
          <Card className="text-center">
            <i className="fa-solid fa-shield-halved text-slate-500 mb-3 block" />
            <p className="font-medium text-white mb-2">Garantie technique</p>
            <p className="text-sm text-slate-400">Correction des défauts de conformité pendant 3 mois</p>
          </Card>
          <Card className="text-center">
            <i className="fa-solid fa-circle-info text-slate-500 mb-3 block" />
            <p className="font-medium text-white mb-2">Limitation</p>
            <p className="text-sm text-slate-400">Responsabilité limitée au montant de la prestation</p>
          </Card>
          <Card className="text-center">
            <i className="fa-solid fa-ban text-slate-500 mb-3 block" />
            <p className="font-medium text-white mb-2">Exclusions</p>
            <p className="text-sm text-slate-400">Dommages indirects et perte d'exploitation exclus</p>
          </Card>
        </Grid>
      </Section>

      <Section number="8" title="Résiliation">
        <p className="text-slate-400 mb-6">Le contrat peut être résilié dans les conditions suivantes :</p>
        <Grid>
          <Card>
            <p className="font-medium text-white mb-3">Résiliation pour faute</p>
            <ul className="list-disc pl-4 text-sm text-slate-400 space-y-2">
              <li>Non-paiement après mise en demeure</li>
              <li>Non-respect des obligations contractuelles</li>
              <li>Liquidation judiciaire de l'une des parties</li>
              <li>Résiliation immédiate sans préavis</li>
            </ul>
          </Card>
          <Card>
            <p className="font-medium text-white mb-3">Résiliation amiable</p>
            <ul className="list-disc pl-4 text-sm text-slate-400 space-y-2">
              <li>Accord mutuel des deux parties</li>
              <li>Préavis de 30 jours calendaires</li>
              <li>Paiement des prestations déjà réalisées</li>
              <li>Remise des éléments existants</li>
            </ul>
          </Card>
        </Grid>
      </Section>

      <Section number="9" title="Droit applicable et juridiction compétente">
        <Grid>
          <Card>
            <p className="font-medium text-white mb-2">
              <i className="fa-solid fa-scale-balanced text-slate-500 mr-2" />
              Droit applicable
            </p>
            <p className="text-sm text-slate-400">Les présentes CGV sont soumises au droit français.</p>
          </Card>
          <Card>
            <p className="font-medium text-white mb-2">
              <i className="fa-solid fa-building-columns text-slate-500 mr-2" />
              Juridiction compétente
            </p>
            <p className="text-sm text-slate-400">En cas de litige, les tribunaux de Nantes seront seuls compétents.</p>
          </Card>
        </Grid>
      </Section>

      <ContactCTA
        title="Questions sur nos conditions de vente ?"
        subtitle="Notre équipe commerciale est disponible pour répondre à vos questions."
        secondaryHref="/mentions-legales"
        secondaryLabel="Mentions légales"
      />
    </LegalLayout>
  )
}
